/* eslint-disable @typescript-eslint/no-explicit-any */
import ArrowCircleLeftIcon from '@mui/icons-material/ArrowCircleLeft';
import SearchIcon from '@mui/icons-material/Search';
import { format } from 'date-fns';
import { ref, update } from 'firebase/database';
import { getDownloadURL, uploadBytesResumable } from 'firebase/storage';
import BlotFormatter from 'quill-blot-formatter';
import ImageResize from 'quill-image-resize-module-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { EditableMathField } from 'react-mathquill';
import ReactQuill from 'react-quill';
import { useParams } from 'react-router-dom';
import { Button, Input } from 'reactstrap';
import { v4 as uuidv4 } from 'uuid';

import { allFormulas, currentFormulasConfig } from './configs';
import CustomImageAlignmentModule from './customQuillModules/CustomImageAlignment';
import CustomStrongBlot from './customQuillModules/CustomStrongBlot';
import HorizontalLine from './customQuillModules/HorizontalLine';
import ToolbarAlt from './customQuillModules/ToolbarAlt';
import VerticalLine from './customQuillModules/VerticalLine';
import WaveLine from './customQuillModules/WaveLine';
import { AddHomeworkButton, AddPhotoButton, AdvancedButtons, FormulasButtonsList, ToolbarButtonsList } from './ui';
import { createNewSheetInNotebook } from '../../api/createNewSheetInNotebook';
import { getHomeworks } from '../../api/getHomeworks';
import { getNotebookData } from '../../api/getNotebookData';
import { getNotebookDataBySheetId } from '../../api/getNotebookDataBySheetId';
import { saveContentString } from '../../api/saveContentString';
import { getStudentHomework, getStudents } from '../../Common/Database';
import { MyRef, myStorage, database } from '../../Common/Firebase';
import Loader from '../../Components/Loader';
import { PopupAddHomework } from '../../Components/PopupAddHomework/PopupAddHomework';
import { PopupHomework } from '../../Components/PopupHomework/PopupHomework';
import {
  HomeworkFromDBType,
  HomeworkOnPageType,
  NewNotebookSheetType,
  NotebookDataType,
} from '../../shared/types/notebookTypes';
import GraphMathPopup from '../Notebook/GraphMathPopup';
import SpreadFindModal from '../Notebook/SpreadFindModal';

import './style.scss';
import 'react-quill/dist/quill.snow.css';

interface NewNotebookProps {
  typeUser: 'Student' | 'Teacher';
  homeworkMail?: string;
}

const formulasPerPage = 6;

function NewNotebook({ typeUser, homeworkMail }: NewNotebookProps): React.JSX.Element {
  const { subject: userSubject, mail: userMail } = useParams<{
    subject?: string;
    mail: string;
  }>();
  const { t } = useTranslation();
  const firstRef: React.MutableRefObject<ReactQuill | null> | undefined = useRef(null);
  const secondRef: React.MutableRefObject<ReactQuill | null> | undefined = useRef(null);

  const [loading, setLoading] = useState<boolean>(true);
  const [title, setTitle] = useState<string | undefined>('');
  const [isSquared, setSquared] = useState<boolean>(true);
  const [toolbarMenuFlag, setToolbarMenuFlag] = useState<boolean>(false);
  const [currentListNumber, setCurrentListNumber] = useState<number>(1);
  const [firstContent, setFirstContent] = useState<string>('');
  const [secondContent, setSecondContent] = useState<string>('');
  const [firstPageOrder, setFirstPageOrder] = useState<number>();
  const [secondPageOrder, setSecondPageOrder] = useState<number>();
  const [firstPageDate, setFirstPageDate] = useState<string>('');
  const [secondPageDate, setSecondPageDate] = useState<string>('');
  const [currentSheetId, setCurrentSheetId] = useState<string>('');
  const [currentEditor, setCurrentEditor] = useState<'first' | 'second'>('first');
  const [showGraphMathPopup, setShowGraphMathPopup] = useState<boolean>(false);
  const [showFormulaPopup, setShowFormulaPopup] = useState<boolean>(false);
  const [currentFormulas, setCurrentFormulas] = useState<string[]>([]);
  const [currentFormulasPage, setCurrentFormulaPage] = useState<number>(0);
  const [showHomeworkAddPopup, setShowHomeworkAddPopup] = useState<boolean>(false);
  const [arrAtributsHomeworksObjNew, setArrAtributsHomeworksObjNew] = useState([]);
  const [exumsContent, setExumsContent] = useState([]);
  const [previousId, setPreviousId] = useState<string | null>(null);
  const [nextId, setNextId] = useState<string | null>(null);
  const [showFind, setShowFind] = useState<boolean>(false);
  const [fullData, setFullData] = useState<NotebookDataType[]>([]);
  const [firstPageReview, setFirstPageReview] = useState<boolean>(false);
  const [secondPageReview, setSecondPageReview] = useState<boolean>(false);
  const [showHomeworkPopup, setShowHomeworkPopup] = useState<boolean>(false);
  const [homeworkContent, setHomeworkContent] = useState<null | { [key: string]: any }>(null);
  const [fullHomeworks, setFullHomeworks] = useState<(HomeworkFromDBType | null)[]>([]);
  const [student, setStudent] = useState<any | null>(null);
  const [firstPageHomework, setFirstPageHomework] = useState<HomeworkOnPageType | null>(null);
  const [secondPageHomework, setSecondPageHomework] = useState<HomeworkOnPageType | null>(null);
  const [addedEventFlag, setAddedEventFlag] = useState<boolean>(false);
  const [notActive, setNotActive] = useState<boolean>(false);

  const areButtonsDisabled = useMemo(
    () => (currentEditor === 'first' && firstPageReview) || (currentEditor === 'second' && secondPageReview),
    [currentEditor, firstPageReview, secondPageReview],
  );

  const Formula = ReactQuill.Quill.import('modules/formula');
  ReactQuill.Quill.register({ 'modules/formula': Formula }, true);
  ReactQuill.Quill.register({
    'formats/customStrong': CustomStrongBlot,
  });
  ReactQuill.Quill.register('modules/toolbar', ToolbarAlt, true);
  ReactQuill.Quill.register('modules/imageResize', ImageResize);
  ReactQuill.Quill.register('modules/customImageAlignment', CustomImageAlignmentModule);
  ReactQuill.Quill.register('modules/blotFormatter', BlotFormatter);
  ReactQuill.Quill.register({
    'formats/horizontal-line': HorizontalLine,
    'formats/vertical-line': VerticalLine,
    'formats/wave-line': WaveLine,
  });

  const indexOfLastFormula = (currentFormulasPage + 1) * formulasPerPage;

  const handleEnterSecond = (range: any, context: any) => {
    const editor = secondRef?.current?.getEditor();

    const lines = editor?.getLines();
    const maxCharsPerLine = isSquared ? 680 : 680;

    let sumHeightLines = 0;
    let sumHeightLinesNotFull = 0;
    for (let i = 0; i < lines.length; i++) {
      const line = lines?.[i];

      sumHeightLines = sumHeightLines += line.domNode.clientHeight;
      sumHeightLinesNotFull = sumHeightLinesNotFull += line.domNode.clientHeight;
    }

    if (sumHeightLines > maxCharsPerLine) {
      if (range.index + 1 == editor?.getLength()) {
        setTimeout(() => {
          nextPage();
        }, 100);
        return;
      }

      editor?.setSelection(range.index + context.suffix.length + 1, 0);

      return;
    }

    editor?.insertText(range.index, '\n');
    editor?.setSelection(range.index + context.suffix.length + 1, 0);

    return true;
  };

  const toolbarForFirst = useMemo(() => {
    return {
      toolbar: {
        container: '#toolbar_first',
      },
      keyboard: {
        bindings: {
          tab: {
            key: 13,
            handler: (range: any, context: any) => {
              handleEnterFirst(range, context);
            },
          },
        },
      },
      blotFormatter: {},
      imageResize: {
        parchment: ReactQuill.Quill.import('parchment'),
        modules: ['Resize'],
      },
      customImageAlignment: true,
    };
  }, []);

  const toolbarForSecond = useMemo(() => {
    return {
      toolbar: {
        container: '#toolbar_second',
      },
      keyboard: {
        bindings: {
          tab: {
            key: 13,
            handler: (range: any, context: any) => {
              handleEnterSecond(range, context);
            },
          },
        },
      },
      blotFormatter: {},
      imageResize: {
        parchment: ReactQuill.Quill.import('parchment'),
        modules: ['Resize'],
      },
      customImageAlignment: true,
    };
  }, [nextId]);

  useEffect(() => {
    setTitle(userSubject);
    setCurrentFormulas(currentFormulasConfig);

    getNotebookData(userMail, userSubject).then(data => {
      if (data && data.length) {
        const initialNotesheet = data.find(it => it.firstContent.order === 1);

        if (initialNotesheet) {
          setFullData(data);

          setSheet(initialNotesheet);
        }
      }
    });

    getStudentHomework(userMail, userSubject, (data: any) => {
      try {
        const mapList: any = Object.values(data.data);
        const exumCont = data.exumsContent;

        setArrAtributsHomeworksObjNew(mapList);
        setExumsContent(exumCont);
      } catch (error) {
        console.error('Error processing data:', error);
      }
    });

    getHomeworks(userSubject).then(data => {
      setFullHomeworks([...data]);
    });

    if (typeUser === 'Teacher') {
      getStudents().then(data => {
        if (data) {
          const targetStudent = data.find(it => {
            const formattedStr = it.email.replace(/[\s.,%]/g, '');

            return formattedStr === userMail;
          });

          if (targetStudent) {
            setStudent(targetStudent);
          }
        }
      });
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    save();
  }, [firstContent, secondContent, nextId, firstPageHomework, secondPageHomework]);

  const setSheet = ({ id, firstContent, secondContent, nextId, prevId, spreadOrder, isSquared }: NotebookDataType) => {
    setFirstContent(firstContent.content);
    setSecondContent(secondContent.content);
    setFirstPageOrder(firstContent.order);
    setSecondPageOrder(secondContent.order);
    setFirstPageDate(firstContent.lastModifiedDate);
    setSecondPageDate(secondContent.lastModifiedDate);
    setCurrentSheetId(id);
    setNextId(nextId === 'null' ? null : nextId);
    setPreviousId(prevId === 'null' ? null : prevId);
    setCurrentListNumber(spreadOrder);
    setFirstPageReview(firstContent.isReviewed);
    setSecondPageReview(secondContent.isReviewed);
    setSquared(isSquared);
    setFirstPageHomework(firstContent.homework ?? null);
    setSecondPageHomework(secondContent.homework ?? null);
  };

  useEffect(() => {
    updateCurrentFormulas();
  }, [currentFormulasPage]);

  const save = async () => {
    saveContentString(
      userSubject,
      userMail,
      currentSheetId,
      firstContent,
      secondContent,
      firstPageDate,
      secondPageDate,
      firstPageOrder,
      secondPageOrder,
      isSquared,
      nextId,
      previousId,
      firstPageReview,
      secondPageReview,
      firstPageHomework,
      secondPageHomework,
    );
  };

  const handleEventFlagFunc = () => {
    setAddedEventFlag(!addedEventFlag);
  };

  const handleOpenHomeworkPopup = (position: 'left' | 'right', homework: HomeworkOnPageType) => {
    const targetId = homework.content;

    setSecondContent(secondContent);
    setFirstContent(firstContent);

    const targetItem = fullHomeworks.find(item => item?.id === targetId);

    if (!showHomeworkPopup) {
      setShowHomeworkPopup(true);

      setHomeworkContent({
        exumsContent: exumsContent,
        arrAtributsHomeworksObjNew: arrAtributsHomeworksObjNew,
        subject: userSubject,
        item: homework.content,
        mail: userMail,
        targetItem: targetItem,
        deadline: homework.deadline,
        number: homework.deadline,
        homeworkName: homework['homework-name'],
        positionNameAction: position,
      });
    }
  };

  const closeHomeworkPopup = () => {
    setShowHomeworkPopup(false);
  };

  const handleMouseLeave = () => {
    if (userSubject && userMail) {
      save();
    }
  };

  const handleEnterFirst = (range: any, context: any) => {
    const editor = firstRef?.current?.getEditor();

    const lines = editor?.getLines();
    const maxCharsPerLine = isSquared ? 680 : 680;

    let sumHeightLines = 0;
    let sumHeightLinesNotFull = 0;

    if (lines) {
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        sumHeightLines = sumHeightLines += line.domNode.clientHeight;
        sumHeightLinesNotFull = sumHeightLinesNotFull += line.domNode.clientHeight;
      }
    }

    if (sumHeightLines > maxCharsPerLine) {
      if (range.index + 1 === editor?.getLength()) {
        secondRef?.current?.getEditor().setSelection(0, 0);
        return;
      }
      editor?.setSelection(range.index + context.suffix.length + 1, 0);

      return;
    }

    editor?.insertText(range.index, '\n');
    editor?.setSelection(range.index + context.suffix.length + 1, 0);
    return true;
  };

  const insertMathSymbol = (symbol: string) => {
    const editor = currentEditor === 'first' ? firstRef?.current?.getEditor() : secondRef?.current?.getEditor();
    const range = editor?.getSelection();

    if (range) {
      editor?.insertText(range.index, symbol);
      editor?.setSelection(range.index + 1);
    }

    save();
  };

  const handleHighlight = () => {
    const editor = currentEditor === 'first' ? firstRef?.current?.getEditor() : secondRef?.current?.getEditor();
    const range = editor?.getSelection();
    if (range && range.length !== 0) {
      editor?.format('customStrong', true);
    }

    save();
  };

  const handleAddGraph = (graphImage: any, action: string) => {
    const editor = action === 'first' ? firstRef?.current?.getEditor() : secondRef?.current?.getEditor();
    const length = editor?.getLength();
    editor?.insertEmbed(length, 'image', graphImage);
    editor?.setSelection(length + 1);
    save();
  };

  const handleChangeFirstContent = (text: string) => {
    const date = new Date();
    const formattedDate = format(date, 'dd.MM.yyyy');

    if (formattedDate !== firstPageDate) {
      setFirstPageDate(formattedDate);
    }

    setFirstContent(text);
  };

  const handleChangeSecondContent = (text: string) => {
    const date = new Date();
    const formattedDate = format(date, 'dd.MM.yyyy');

    if (formattedDate !== secondPageDate) {
      setSecondPageDate(formattedDate);
    }

    setSecondContent(text);
  };

  const handleShowGraphMathPopup = () => {
    setShowGraphMathPopup(!showGraphMathPopup);
  };

  const handleShowFormulaPopup = () => {
    setShowFormulaPopup(!showFormulaPopup);
  };

  const updateCurrentFormulas = () => {
    const indexOfLastFormula = (currentFormulasPage + 1) * formulasPerPage;
    const indexOfFirstFormula = indexOfLastFormula - formulasPerPage;
    const newFormulasOnPage = allFormulas.slice(indexOfFirstFormula, indexOfLastFormula);

    setCurrentFormulas(newFormulasOnPage);
  };

  const handleFormulaChange = (index: number, newLatex: string) => {
    const newFormulas = [...currentFormulas];
    newFormulas[index] = newLatex;
    setCurrentFormulas(newFormulas);
  };

  const addFormulaToEditor = (formula: string) => {
    const editor = currentEditor === 'first' ? firstRef?.current?.getEditor() : secondRef?.current?.getEditor();

    const range = editor?.getSelection();

    if (range) {
      editor?.insertEmbed(range.index, 'formula', formula);
      editor?.setSelection(range.index + 1);
      save();
    }
  };

  const renderFormulas = () => {
    return currentFormulas.map((formula, index) => {
      return (
        <div key={index} className="formulas-popup-content-item">
          <EditableMathField
            latex={formula}
            onChange={mathField => {
              handleFormulaChange(index, mathField.latex());
            }}
          />
          <button onClick={() => addFormulaToEditor(formula)}>{t('add')}</button>
        </div>
      );
    });
  };

  const prevFormulaPage = () => {
    setCurrentFormulaPage(currentFormulasPage - 1);
  };

  const nextFormulasPage = () => {
    setCurrentFormulaPage(currentFormulasPage + 1);
  };

  const handleShowPopupAddHomework = (editorFlag: 'first' | 'second') => {
    setShowHomeworkAddPopup(!showHomeworkAddPopup);
    setCurrentEditor(editorFlag);
  };

  const handleClosePopupAddHomework = () => {
    setShowHomeworkAddPopup(false);
  };

  const addHomeworkToContent = (obj: any) => {
    switch (currentEditor) {
      case 'second':
        setTimeout(() => {
          const editor = secondRef?.current?.getEditor();

          setSecondPageHomework({
            'homework-name': `${obj.name}`,
            content: obj.id,
            deadline: obj.deadline,
          });

          save();
        }, 0);

        break;

      case 'first':
        setTimeout(() => {
          const editor = firstRef?.current?.getEditor();

          setFirstPageHomework({
            'homework-name': `${obj.name}`,
            content: obj.id,
            deadline: obj.deadline,
          });

          save();
        }, 0);

        break;
    }

    setShowHomeworkAddPopup(false);
    save();
  };

  const handleAddImage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const id = uuidv4();
    // @ts-ignore
    const file = event?.target?.files[0];

    if (file) {
      const formData = new FormData();
      formData.append('image', file);

      const storageRef = MyRef(myStorage, 'Домашние-задания/' + 'image-text' + '/' + id + '/' + file.name);

      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        'state_changed',
        snapshot => {
          switch (snapshot.state) {
            case 'paused':
              console.log('Upload is paused');
              break;
            case 'running':
              console.log('Upload is running');
              break;
          }
        },
        error => {
          switch (error.code) {
            case 'storage/unauthorized':
              break;
            case 'storage/canceled':
              break;
            case 'storage/unknown':
              break;
          }
        },
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then(downloadURL => {
            const imageUrl = downloadURL;
            const editor = currentEditor === 'first' ? firstRef?.current?.getEditor() : secondRef?.current?.getEditor();

            const range = editor?.getSelection();
            const index = range ? range.index : 0;

            editor?.clipboard.dangerouslyPasteHTML(
              index,
              `<img src="${imageUrl}" alt="Image" style="display: inline; float: left;"/>`,
            );

            save();
          });
        },
      );
    }
  };

  const nextPage = async () => {
    setLoading(true);

    setNotActive(true);

    const id = uuidv4();
    const now = new Date();
    const dateTimeParsing = format(now.setHours(now.getHours()), 'dd.MM.yyyy');

    if (!nextId && firstPageOrder && secondPageOrder && userSubject && userMail) {
      setNextId(id);

      const nextItem: NewNotebookSheetType = {
        id: id,
        isSquared: isSquared,
        firstSheet: {
          content: id,
          isReviewed: false,
          lastModifiedDate: dateTimeParsing,
          order: firstPageOrder + 2,
        },
        secondSheet: {
          content: id,
          isReviewed: false,
          lastModifiedDate: dateTimeParsing,
          order: secondPageOrder + 2,
        },
        nextId: 'null',
        prevId: currentSheetId,
      };

      await createNewSheetInNotebook(nextItem, userMail, userSubject, id);

      await getNotebookDataBySheetId(userMail, userSubject, id).then(data => {
        if (data) {
          setSheet(data);

          setLoading(false);

          setNotActive(false);
        }
      });
    } else {
      if (nextId) {
        await getNotebookDataBySheetId(userMail, userSubject, nextId).then(data => {
          if (data) {
            setSheet(data);

            setLoading(false);

            setNotActive(false);
          }
        });
      }
    }
  };

  const prevPage = async () => {
    if (previousId) {
      setLoading(true);

      await getNotebookDataBySheetId(userMail, userSubject, previousId).then(data => {
        if (data) {
          setSheet(data);

          setLoading(false);
        }
      });
    }
  };

  const showFindModal = () => {
    getNotebookData(userMail, userSubject).then(data => {
      if (data && data.length) {
        setFullData(data);

        setShowFind(true);
      }
    });
  };

  const hideFindModal = () => {
    setShowFind(false);
  };

  const onSearchMethod = (value: number | string, switchValue: string) => {
    switch (switchValue) {
      case 'page-order':
        const findDataByPage = fullData.find(item => {
          if (item.firstContent.order === value || item.secondContent.order === value) {
            return item;
          }
        });

        if (findDataByPage) {
          setSheet(findDataByPage);
        }
        break;
      case 'date':
        const findDataByDate = fullData.find(item => {
          if (item.firstContent.lastModifiedDate === value || item.secondContent.lastModifiedDate === value) {
            return item;
          }
        });

        if (findDataByDate) {
          setSheet(findDataByDate);
        }
        break;
    }
  };

  const handleReviewed = (value: boolean, name: 'first' | 'second') => {
    const now = new Date();
    const formattedTime = format(now, 'dd MM yyyy HH:mm');

    if (name === 'first') {
      setFirstPageReview(value);
    } else {
      setSecondPageReview(value);
    }

    if (value) {
      const notificationId = uuidv4();

      update(ref(database, `Students/${userMail}/notification`), {
        [notificationId + userMail]: {
          id: notificationId + userMail,
          idItem: currentSheetId + userMail,
          subject: userSubject,
          name: '',
          time: formattedTime,
          type: 'notebook',
          order: name === 'first' ? firstPageOrder : secondPageOrder,
          viewed: true,
        },
      });
    }

    save();
  };

  const handleDeleteHomeworkFromPage = (position: 'left' | 'right') => {
    if (position === 'right') {
      setFirstPageHomework(null);
    } else {
      setSecondPageHomework(null);
    }
  };

  const renderStudentName = () => {
    if (typeUser === 'Teacher' && student) {
      return `${student.FIO}`;
    }
  };

  const renderHomeworkButton = (obj: HomeworkOnPageType | null, position: 'left' | 'right') => {
    if (obj) {
      return (
        <div className="openHomeworkPopupButtonWrapper">
          <button className="openHomeworkPopupButton" onClick={() => handleOpenHomeworkPopup(position, obj)}>
            {t('homework')} - {obj['homework-name']}
          </button>

          {typeUser === 'Student' && (
            <span
              onClick={() => handleDeleteHomeworkFromPage(position)}
              className="icon icon-plus deleteHomeworkButton"
            ></span>
          )}
        </div>
      );
    } else {
      return;
    }
  };

  // @ts-ignore
  return loading ? (
    <Loader progress={undefined} />
  ) : (
    <div className={isSquared ? 'notebook-page-p container' : 'notebook-page-p squared container'}>
      <div className="title-wrap">
        <h1>
          {t('notebook-title')} {t(`${title}`)} {renderStudentName()}
        </h1>
      </div>

      {showFormulaPopup && (
        <div className={currentEditor === 'second' ? 'formulas-popup left' : 'formulas-popup'}>
          <div className="formulas-popup-content">
            <button className="btn btn-icon close-btn" onClick={() => handleShowFormulaPopup()}>
              <span className="icon icon-plus"></span>
            </button>

            {renderFormulas()}

            <div>
              {currentFormulasPage > 0 && <button onClick={() => prevFormulaPage()} className="prev-btn"></button>}
              {indexOfLastFormula < allFormulas.length && (
                <button onClick={() => nextFormulasPage()} className="next-btn"></button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className={typeUser == 'Teacher' ? 'notebook-background teacher' : 'notebook-background student'}>
        <div className="notebook-background-image background"></div>

        <Row className={'toolbar-row student'}>
          {typeUser === 'Student' ? (
            <>
              <Col className={toolbarMenuFlag ? 'toolbar-container active' : 'toolbar-container'} xs={9}>
                <ToolbarButtonsList
                  id="toolbar_first"
                  style={
                    currentEditor === 'first'
                      ? { display: 'flex', overflow: 'hidden', overflowX: 'auto' }
                      : { display: 'none' }
                  }
                  isDisabled={firstPageReview}
                />
                <ToolbarButtonsList
                  id="toolbar_second"
                  style={
                    currentEditor === 'second'
                      ? { display: 'flex', overflow: 'hidden', overflowX: 'auto' }
                      : { display: 'none' }
                  }
                  isDisabled={secondPageReview}
                />

                <Col className="margin-auto text-align-center search-btn" xs={1}>
                  {isSquared && (
                    <AdvancedButtons
                      onHandleHighlight={handleHighlight}
                      onHandleShowFormulaPopup={handleShowFormulaPopup}
                      onHandleShowGraphMathPopup={handleShowGraphMathPopup}
                      isDisabled={areButtonsDisabled}
                    />
                  )}

                  <Button onClick={showFindModal}>
                    <SearchIcon />
                    {t('search')}
                  </Button>

                  <Button
                    className={toolbarMenuFlag ? 'arrow-toolbar-menu active' : 'arrow-toolbar-menu'}
                    onClick={e => setToolbarMenuFlag(!toolbarMenuFlag)}
                  >
                    <ArrowCircleLeftIcon />
                  </Button>
                </Col>

                {isSquared && <FormulasButtonsList isDisabled={areButtonsDisabled} onClick={insertMathSymbol} />}
              </Col>
            </>
          ) : (
            <>
              <Col className={toolbarMenuFlag ? 'toolbar-container active' : 'toolbar-container'} xs={9}>
                <ToolbarButtonsList
                  id="toolbar_first"
                  style={
                    currentEditor === 'first'
                      ? { display: 'flex', overflow: 'hidden', overflowX: 'auto' }
                      : { display: 'none' }
                  }
                />
                <ToolbarButtonsList
                  id="toolbar_second"
                  style={
                    currentEditor === 'second'
                      ? { display: 'flex', overflow: 'hidden', overflowX: 'auto' }
                      : { display: 'none' }
                  }
                />

                <Col className="margin-auto text-align-center search-btn" xs={1}>
                  <Button className="find-btn" onClick={showFindModal}>
                    <SearchIcon />
                  </Button>

                  <Button
                    className={toolbarMenuFlag ? 'arrow-toolbar-menu active' : 'arrow-toolbar-menu'}
                    onClick={e => setToolbarMenuFlag(!toolbarMenuFlag)}
                  >
                    <ArrowCircleLeftIcon />
                  </Button>
                </Col>
              </Col>
            </>
          )}
        </Row>

        <Row className="arrow-container" style={firstPageHomework || secondPageHomework ? { margin: '0 0 75px' } : {}}>
          <div className="wrap-block-arrow">
            {previousId && <i className="arrow arrow-left" onClick={prevPage}></i>}
            <i className="arrow" onClick={() => nextPage()}></i>
          </div>
        </Row>

        <>
          <Row onMouseLeave={() => handleMouseLeave()} className="row-notebock">
            <Col className={'notebook-page'}>
              <div className={isSquared ? 'page-container squared' : 'page-container'}>
                <div className="date-container">{`Дата: ${firstPageDate}`}</div>

                {typeUser === 'Teacher' && (
                  <div className="checked-container">
                    <Input
                      className="homework-checkbox-input"
                      checked={firstPageReview}
                      readOnly={typeUser !== 'Teacher'}
                      onChange={e => handleReviewed(e.target.checked, 'first')}
                      type="checkbox"
                    />
                    {t('checked')}
                  </div>
                )}
                {typeUser === 'Student' && firstPageReview && (
                  <div className="checked-container right student">
                    <h3>{t('checked')}</h3>
                  </div>
                )}

                {typeUser === 'Student' && (
                  <>
                    <AddPhotoButton onChange={handleAddImage} isDisabled={areButtonsDisabled} />
                    <AddHomeworkButton
                      onClick={() => handleShowPopupAddHomework('first')}
                      isDisabled={areButtonsDisabled}
                      homework={firstPageHomework || secondPageHomework}
                    />
                  </>
                )}

                {renderHomeworkButton(firstPageHomework, 'right')}

                {!notActive && (
                  <ReactQuill
                    className="quill-first"
                    value={firstContent}
                    onChange={handleChangeFirstContent}
                    modules={toolbarForFirst}
                    ref={firstRef}
                    readOnly={typeUser === 'Student' && firstPageReview}
                    onFocus={() => setCurrentEditor('first')}
                    theme="snow"
                  />
                )}

                <div className="page-order right-order">{firstPageOrder}</div>
              </div>
            </Col>

            <Col className={currentListNumber === 2 ? 'notebook-page left active' : 'notebook-page left'} xs={6}>
              <div className={isSquared ? 'page-container squared' : 'page-container'}>
                <div className="date-container">{`Дата: ${secondPageDate}`}</div>

                {typeUser === 'Teacher' && (
                  <div className="checked-container">
                    <Input
                      className="homework-checkbox-input"
                      checked={secondPageReview}
                      readOnly={typeUser !== 'Teacher'}
                      onChange={e => handleReviewed(e.target.checked, 'second')}
                      type="checkbox"
                    />
                    {t('checked')}
                  </div>
                )}
                {typeUser === 'Student' && secondPageReview && (
                  <>
                    <div className="checked-container right student">
                      <h3>{t('checked')}</h3>
                    </div>
                  </>
                )}

                {typeUser === 'Student' && (
                  <>
                    <AddPhotoButton onChange={handleAddImage} isDisabled={areButtonsDisabled} />
                    <AddHomeworkButton
                      onClick={() => handleShowPopupAddHomework('second')}
                      isDisabled={areButtonsDisabled}
                      homework={firstPageHomework || secondPageHomework}
                    />
                  </>
                )}

                {renderHomeworkButton(secondPageHomework, 'left')}

                {!notActive && (
                  <ReactQuill
                    className="quill-second"
                    value={secondContent}
                    onChange={handleChangeSecondContent}
                    modules={toolbarForSecond}
                    ref={secondRef}
                    readOnly={typeUser === 'Student' && secondPageReview}
                    onFocus={() => setCurrentEditor('second')}
                    theme="snow"
                  />
                )}

                <div className="page-order">{secondPageOrder}</div>
              </div>
            </Col>
          </Row>
        </>

        {showGraphMathPopup && (
          <GraphMathPopup
            isOpen={showGraphMathPopup}
            onClose={handleShowGraphMathPopup}
            nameAction={currentEditor}
            onAddGraph={handleAddGraph}
          />
        )}

        {showHomeworkAddPopup && (
          <PopupAddHomework
            addHomeworkToContent={addHomeworkToContent}
            arrObjAtributs={arrAtributsHomeworksObjNew}
            closeHomework={handleClosePopupAddHomework}
            nameContent={currentEditor}
            firstContent={firstContent}
            secondContent={secondContent}
          />
        )}

        {showFind && (
          <SpreadFindModal
            onCloseFunc={hideFindModal}
            fullData={fullData}
            onSearchMetod={onSearchMethod}
          ></SpreadFindModal>
        )}

        {showHomeworkPopup && homeworkContent && (
          <PopupHomework
            homeworkObj={homeworkContent}
            closeHomework={closeHomeworkPopup}
            saveContent={save}
            handleEvent={handleEventFlagFunc}
          />
        )}
      </div>
    </div>
  );
}

export default NewNotebook;
